// Performance optimization utilities

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Throttle function to limit function call frequency
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Memoization utility for expensive calculations
 * @param {Function} fn - Function to memoize
 * @param {Function} keyGenerator - Optional key generator function
 * @returns {Function} Memoized function
 */
export const memoize = (fn, keyGenerator = (...args) => JSON.stringify(args)) => {
  const cache = new Map();
  
  return (...args) => {
    const key = keyGenerator(...args);
    
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = fn(...args);
    cache.set(key, result);
    
    // Limit cache size to prevent memory leaks
    if (cache.size > 100) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    
    return result;
  };
};

/**
 * Lazy loading utility for images and components
 */
export const lazyLoader = {
  /**
   * Lazy load images with intersection observer
   * @param {HTMLImageElement} img - Image element
   * @param {string} src - Image source
   * @param {Object} options - Intersection observer options
   */
  loadImage: (img, src, options = {}) => {
    const defaultOptions = {
      rootMargin: '50px',
      threshold: 0.1,
      ...options
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const image = entry.target;
          image.src = src;
          image.classList.remove('lazy-loading');
          image.classList.add('lazy-loaded');
          observer.unobserve(image);
        }
      });
    }, defaultOptions);

    img.classList.add('lazy-loading');
    observer.observe(img);

    return () => observer.unobserve(img);
  },

  /**
   * Create placeholder for lazy loading
   * @param {number} width - Placeholder width
   * @param {number} height - Placeholder height
   * @returns {string} Data URL for placeholder
   */
  createPlaceholder: (width = 400, height = 200) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#f3f4f6');
    gradient.addColorStop(1, '#e5e7eb');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    return canvas.toDataURL();
  }
};

/**
 * Performance monitoring utilities
 */
export const performanceMonitor = {
  /**
   * Measure function execution time
   * @param {Function} fn - Function to measure
   * @param {string} label - Label for the measurement
   * @returns {Function} Wrapped function
   */
  measureFunction: (fn, label = 'Function') => {
    return (...args) => {
      const start = performance.now();
      const result = fn(...args);
      const end = performance.now();
      
      console.log(`${label} execution time: ${(end - start).toFixed(2)}ms`);
      
      // Track performance metrics
      performanceMonitor.trackMetric(label, end - start);
      
      return result;
    };
  },

  /**
   * Track performance metrics
   * @param {string} name - Metric name
   * @param {number} value - Metric value
   */
  trackMetric: (name, value) => {
    if (!window.performanceMetrics) {
      window.performanceMetrics = {};
    }
    
    if (!window.performanceMetrics[name]) {
      window.performanceMetrics[name] = [];
    }
    
    window.performanceMetrics[name].push({
      value,
      timestamp: Date.now()
    });
    
    // Keep only last 100 measurements
    if (window.performanceMetrics[name].length > 100) {
      window.performanceMetrics[name].shift();
    }
  },

  /**
   * Get performance statistics
   * @param {string} name - Metric name
   * @returns {Object} Performance statistics
   */
  getStats: (name) => {
    const metrics = window.performanceMetrics?.[name] || [];
    
    if (metrics.length === 0) {
      return null;
    }
    
    const values = metrics.map(m => m.value);
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = sum / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    return {
      count: metrics.length,
      average: avg,
      min,
      max,
      total: sum
    };
  },

  /**
   * Monitor Core Web Vitals
   */
  monitorWebVitals: () => {
    // Largest Contentful Paint (LCP)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      performanceMonitor.trackMetric('LCP', lastEntry.startTime);
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay (FID)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        performanceMonitor.trackMetric('FID', entry.processingStart - entry.startTime);
      });
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
          performanceMonitor.trackMetric('CLS', clsValue);
        }
      });
    }).observe({ entryTypes: ['layout-shift'] });
  }
};

/**
 * Memory management utilities
 */
export const memoryManager = {
  /**
   * Create a memory-aware cache
   * @param {number} maxSize - Maximum cache size
   * @returns {Object} Cache instance
   */
  createCache: (maxSize = 50) => {
    const cache = new Map();
    let accessOrder = [];

    return {
      get: (key) => {
        if (cache.has(key)) {
          // Update access order (LRU)
          accessOrder = accessOrder.filter(k => k !== key);
          accessOrder.push(key);
          return cache.get(key);
        }
        return undefined;
      },

      set: (key, value) => {
        if (cache.has(key)) {
          // Update existing entry
          cache.set(key, value);
          accessOrder = accessOrder.filter(k => k !== key);
          accessOrder.push(key);
        } else {
          // Add new entry
          if (cache.size >= maxSize) {
            // Remove least recently used item
            const lru = accessOrder.shift();
            cache.delete(lru);
          }
          cache.set(key, value);
          accessOrder.push(key);
        }
      },

      delete: (key) => {
        cache.delete(key);
        accessOrder = accessOrder.filter(k => k !== key);
      },

      clear: () => {
        cache.clear();
        accessOrder = [];
      },

      size: () => cache.size,

      has: (key) => cache.has(key)
    };
  },

  /**
   * Monitor memory usage
   * @returns {Object} Memory information
   */
  getMemoryInfo: () => {
    if ('memory' in performance) {
      return {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
        usagePercentage: (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100
      };
    }
    return null;
  },

  /**
   * Clean up unused resources
   */
  cleanup: () => {
    // Clear expired caches
    if (window.performanceMetrics) {
      const now = Date.now();
      const expiry = 5 * 60 * 1000; // 5 minutes
      
      Object.keys(window.performanceMetrics).forEach(key => {
        window.performanceMetrics[key] = window.performanceMetrics[key].filter(
          metric => now - metric.timestamp < expiry
        );
      });
    }

    // Suggest garbage collection if available
    if (window.gc) {
      window.gc();
    }
  }
};

/**
 * Bundle size optimization utilities
 */
export const bundleOptimizer = {
  /**
   * Dynamic import wrapper with error handling
   * @param {Function} importFn - Dynamic import function
   * @param {string} chunkName - Chunk name for debugging
   * @returns {Promise} Import promise
   */
  lazyImport: async (importFn, chunkName = 'unknown') => {
    try {
      const start = performance.now();
      const module = await importFn();
      const end = performance.now();
      
      console.log(`Loaded chunk "${chunkName}" in ${(end - start).toFixed(2)}ms`);
      performanceMonitor.trackMetric(`chunk-${chunkName}`, end - start);
      
      return module;
    } catch (error) {
      console.error(`Failed to load chunk "${chunkName}":`, error);
      throw error;
    }
  },

  /**
   * Preload critical resources
   * @param {Array} resources - Array of resource URLs
   */
  preloadResources: (resources) => {
    resources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource.url;
      link.as = resource.as || 'fetch';
      if (resource.type) {
        link.type = resource.type;
      }
      document.head.appendChild(link);
    });
  },

  /**
   * Prefetch resources for future navigation
   * @param {Array} resources - Array of resource URLs
   */
  prefetchResources: (resources) => {
    resources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = resource;
      document.head.appendChild(link);
    });
  }
};

/**
 * Frame rate optimization
 */
export const frameOptimizer = {
  /**
   * Request animation frame with fallback
   * @param {Function} callback - Callback function
   * @returns {number} Frame ID
   */
  requestFrame: (callback) => {
    if (window.requestAnimationFrame) {
      return window.requestAnimationFrame(callback);
    } else {
      return setTimeout(callback, 16); // ~60fps fallback
    }
  },

  /**
   * Cancel animation frame
   * @param {number} frameId - Frame ID to cancel
   */
  cancelFrame: (frameId) => {
    if (window.cancelAnimationFrame) {
      window.cancelAnimationFrame(frameId);
    } else {
      clearTimeout(frameId);
    }
  },

  /**
   * Batch DOM updates for better performance
   * @param {Function} updateFn - Function containing DOM updates
   */
  batchDOMUpdates: (updateFn) => {
    frameOptimizer.requestFrame(() => {
      // Batch reads
      const measurements = [];
      
      // Batch writes
      frameOptimizer.requestFrame(() => {
        updateFn(measurements);
      });
    });
  },

  /**
   * Monitor frame rate
   * @param {Function} callback - Callback with FPS data
   */
  monitorFPS: (callback) => {
    let lastTime = performance.now();
    let frameCount = 0;
    let fps = 0;

    const measureFPS = (currentTime) => {
      frameCount++;
      
      if (currentTime - lastTime >= 1000) { // Every second
        fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        callback(fps);
        performanceMonitor.trackMetric('FPS', fps);
        
        frameCount = 0;
        lastTime = currentTime;
      }
      
      frameOptimizer.requestFrame(measureFPS);
    };

    frameOptimizer.requestFrame(measureFPS);
  }
};

/**
 * Network optimization utilities
 */
export const networkOptimizer = {
  /**
   * Check network connection
   * @returns {Object} Network information
   */
  getNetworkInfo: () => {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      };
    }
    return null;
  },

  /**
   * Adaptive loading based on network conditions
   * @param {Object} options - Loading options
   * @returns {Object} Adaptive settings
   */
  getAdaptiveSettings: (options = {}) => {
    const network = networkOptimizer.getNetworkInfo();
    const memory = memoryManager.getMemoryInfo();
    
    let settings = {
      imageQuality: 0.8,
      preloadCount: 3,
      cacheSize: 50,
      animationEnabled: true,
      ...options
    };

    // Adjust based on network
    if (network) {
      if (network.saveData || network.effectiveType === 'slow-2g' || network.effectiveType === '2g') {
        settings = {
          ...settings,
          imageQuality: 0.4,
          preloadCount: 1,
          cacheSize: 20,
          animationEnabled: false
        };
      } else if (network.effectiveType === '3g') {
        settings = {
          ...settings,
          imageQuality: 0.6,
          preloadCount: 2,
          cacheSize: 30
        };
      }
    }

    // Adjust based on memory
    if (memory && memory.usagePercentage > 80) {
      settings = {
        ...settings,
        cacheSize: Math.max(10, Math.floor(settings.cacheSize * 0.5)),
        preloadCount: Math.max(1, Math.floor(settings.preloadCount * 0.5))
      };
    }

    return settings;
  },

  /**
   * Compress image data URL
   * @param {string} dataUrl - Original data URL
   * @param {number} quality - Compression quality (0-1)
   * @returns {string} Compressed data URL
   */
  compressImage: (dataUrl, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };
      
      img.src = dataUrl;
    });
  }
};

// Initialize performance monitoring
if (typeof window !== 'undefined') {
  performanceMonitor.monitorWebVitals();
  
  // Clean up resources periodically
  setInterval(() => {
    memoryManager.cleanup();
  }, 5 * 60 * 1000); // Every 5 minutes
}

const performanceUtils = {
  debounce,
  throttle,
  memoize,
  lazyLoader,
  performanceMonitor,
  memoryManager,
  bundleOptimizer,
  frameOptimizer,
  networkOptimizer
};

export default performanceUtils;